import React from "react";
import { Star, X, ChevronDown } from "lucide-react";

const SORT_OPTIONS = [
  "По популярности",
  "По оценке",
  "По названию",
  "По кол-ву звёзд",
  "По цене (возраст.)",
  "По цене (убыв.)",
];

const HOTEL_TYPES = [
  "Все типы",
  "Отель",
  "Гостевой дом",
  "Апартаменты",
  "Хостел",
  "Глемпинг",
  "Санаторий",
  "Коттедж",
];

const AMENITY_ICONS_LABELS = [
  "Бесплатный Wi-Fi",
  "Парковка",
  "Ресторан",
  "Бассейн",
  "Можно с питомцами",
  "Семейные номера",
];

function RatingSlider({ minRating, setMinRating }) {
  const val = minRating !== null ? minRating : 0;
  const pct = (val / 5) * 100;
  const hue = Math.round((val / 5) * 120);
  const thumbColor = `hsl(${hue}, 85%, 42%)`;
  return (
    <div className="px-1">
      <input
        type="range"
        min={0}
        max={5}
        step={0.1}
        value={val}
        onChange={(e) =>
          setMinRating(
            Number(e.target.value) === 0 ? null : Number(e.target.value),
          )
        }
        className="w-full h-1.5 rounded-full outline-none cursor-pointer appearance-none"
        style={{
          background: `linear-gradient(to right, ${thumbColor} 0%, ${thumbColor} ${pct}%, #e5e7eb ${pct}%, #e5e7eb 100%)`,
          accentColor: thumbColor,
        }}
      />
      <div className="flex justify-between text-[10px] text-gray-400 mt-1 px-0.5">
        <span>0</span>
        <span>1</span>
        <span>2</span>
        <span>3</span>
        <span>4</span>
        <span>5</span>
      </div>
    </div>
  );
}

export default function DesktopFiltersPanel({
  cityData,
  selectedType,
  setSelectedType,
  priceFrom,
  setPriceFrom,
  priceTo,
  setPriceTo,
  minRating,
  setMinRating,
  minStars,
  setMinStars,
  maxSeaDistance,
  setMaxSeaDistance,
  withDog,
  setWithDog,
  selectedAmenities,
  toggleAmenity,
  onResetAmenities,
  sortBy,
  setSortBy,
  onApplyFilters, // Новая функция для применения фильтров
  onResetFilters, // Новая функция для сброса
}) {
  const [showSortDropdown, setShowSortDropdown] = React.useState(false);
  const sortRef = React.useRef(null);
  const debounceTimeout = React.useRef(null);

  React.useEffect(() => {
    const handler = (e) => {
      if (sortRef.current && !sortRef.current.contains(e.target))
        setShowSortDropdown(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const hasActive =
    selectedAmenities.length > 0 ||
    selectedType !== "Все типы" ||
    priceFrom ||
    priceTo ||
    minRating ||
    minStars > 0 ||
    maxSeaDistance ||
    withDog;

  // Обработчики с немедленным применением
  const handlePriceChange = (from, to) => {
    setPriceFrom(from);
    setPriceTo(to);
    onApplyFilters?.({ min_price: from, max_price: to });
  };

  const handleRatingChange = (rating) => {
    setMinRating(rating);
    onApplyFilters?.({ meta_rating: rating });
  };

  const handleStarsChange = (stars) => {
    setMinStars(stars);
    onApplyFilters?.({ star_rating: stars });
  };

  const handleDistanceChange = (distance) => {
    setMaxSeaDistance(distance);
    onApplyFilters?.({ distance });
  };

  const handleDogChange = (value) => {
    setWithDog(value);
    onApplyFilters?.({ dogfriendly: value });
  };

  const resetAll = () => {
    setSelectedType("Все типы");
    setPriceFrom("");
    setPriceTo("");
    setMinRating(null);
    setMinStars(0);
    setMaxSeaDistance("");
    setWithDog(false);
    if (onResetAmenities) onResetAmenities();
    onResetFilters?.();
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 space-y-5">
      {/* Сортировка */}
      {sortBy !== undefined && (
        <div>
          <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">
            Сортировка
          </p>
          <div className="relative" ref={sortRef}>
            <button
              onClick={() => setShowSortDropdown((v) => !v)}
              className="w-full h-8 flex items-center justify-between px-3 rounded-lg border border-gray-200 bg-white text-xs text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <span className="truncate">{sortBy}</span>
              <ChevronDown className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 ml-1" />
            </button>
            {showSortDropdown && (
              <div className="absolute left-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-xl shadow-xl py-1 w-full">
                {SORT_OPTIONS.map((o) => (
                  <button
                    key={o}
                    onClick={() => {
                      setSortBy(o);
                      setShowSortDropdown(false);
                    }}
                    className={`w-full text-left px-3 py-2 text-xs transition-colors ${sortBy === o ? "text-blue-600 font-semibold bg-blue-50" : "text-gray-700 hover:bg-gray-50"}`}
                  >
                    {o}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="border-t border-gray-100" />

      {/* Тип объекта */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Тип объекта
          </p>
          {hasActive && (
            <button
              onClick={resetAll}
              className="flex items-center gap-1 text-xs text-red-500 hover:text-red-600 transition-colors"
            >
              <X className="w-3 h-3" /> Сбросить
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {HOTEL_TYPES.map((type) => (
            <button
              key={type}
              onClick={() => setSelectedType(type)}
              className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${selectedType === type ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"}`}
            >
              {type}
            </button>
          ))}
        </div>
      </div>

      <div className="border-t border-gray-100" />

      {/* Цена */}
      <div>
        <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">
          Цена за ночь, ₽
        </p>
        <div className="flex items-center gap-2">
          <input
            type="number"
            value={priceFrom}
            onChange={(e) => handlePriceChange(e.target.value, priceTo)}
            placeholder="от"
            className="w-full h-8 rounded-lg border border-gray-200 px-2 text-sm text-gray-700 outline-none focus:border-blue-400"
          />
          <span className="text-gray-400 text-sm flex-shrink-0">—</span>
          <input
            type="number"
            value={priceTo}
            onChange={(e) => handlePriceChange(priceFrom, e.target.value)}
            placeholder="до"
            className="w-full h-8 rounded-lg border border-gray-200 px-2 text-sm text-gray-700 outline-none focus:border-blue-400"
          />
          <button
            onClick={() => handlePriceChange(priceFrom, priceTo)}
            className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
          >
            OK
          </button>
        </div>
      </div>

      <div className="border-t border-gray-100" />

      {/* Оценка */}
      <div>
        <p className="text-xs font-semibold text-gray-500 mb-3 uppercase tracking-wide">
          Оценка, от {minRating || "—"}
        </p>
        <RatingSlider minRating={minRating} setMinRating={handleRatingChange} />
      </div>

      <div className="border-t border-gray-100" />

      {/* Звёзды */}
      <div>
        <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">
          Количество звёзд
        </p>
        <div className="flex items-center gap-1">
          {[1, 2, 3, 4, 5].map((s) => (
            <button
              key={s}
              onClick={() => {
                const newStars = minStars === s ? 0 : s;
                handleStarsChange(newStars);
              }}
            >
              <Star
                className={`w-6 h-6 transition-colors ${s <= minStars ? "text-yellow-400 fill-yellow-400" : "text-gray-300 fill-gray-100"}`}
              />
            </button>
          ))}
        </div>
      </div>

      <div className="border-t border-gray-100" />

      {/* Удалённость от моря */}
      <div>
        <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">
          {cityData?.sea ? "До моря" : "До центра"}, не более
        </p>
        <div className="flex items-center gap-2">
          <input
            type="number"
            value={maxSeaDistance}
            onChange={(e) => handleDistanceChange(e.target.value)}
            placeholder="500"
            className="w-24 h-8 rounded-lg border border-gray-200 px-2 text-sm text-gray-700 outline-none focus:border-blue-400"
          />
          <span className="text-sm text-gray-500">м</span>
          <button
            onClick={() => handleDistanceChange(maxSeaDistance)}
            className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
          >
            OK
          </button>
        </div>
      </div>

      <div className="border-t border-gray-100" />

      {/* Удобства */}
      <div>
        <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">
          Удобства
        </p>
        <div className="flex flex-col gap-2">
          {AMENITY_ICONS_LABELS.map((a) => (
            <label
              key={a}
              className="flex items-center gap-2 cursor-pointer group"
            >
              <input
                type="checkbox"
                checked={selectedAmenities.includes(a)}
                onChange={() => toggleAmenity(a)}
                className="w-4 h-4 rounded border-gray-300 text-blue-600 cursor-pointer accent-blue-600"
              />
              <span
                className={`text-sm transition-colors ${selectedAmenities.includes(a) ? "text-blue-600 font-medium" : "text-gray-600 group-hover:text-gray-800"}`}
              >
                {a}
              </span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}
